import React, { ReactElement, useState } from 'react';
import {
  Schedule,
  ScheduleWorker,
  JobsiteScheduledWorkerFormFields,
  ScheduleShift,
} from 'containers/jobsite/tabs/jobsiteSchedules/data/types';
import { FormOnSubmit, ModalForm } from 'components/form';
import { useIsMounted } from 'utils';
import { AlertService } from 'components/alertService';
import {
  GetScheduleDetailsDocument,
  useCreateScheduleWorkerMutation,
  useUpdateScheduleWorkerMutation,
} from 'apollo/generated/client-operations';
import { getGraphQLError } from 'utils/error';
import {
  useJobsiteContractorsOptions,
  useJobsiteContractorWorkersOptions,
} from 'containers/utils';
import {
  getAddInput,
  getDefaultValues,
  getFormInputsHook,
  getUpdateInput,
  useSelectOptions,
} from './EditScheduleWorkerModal.forms';

export function EditScheduleWorkerModal(
  props: {
    isOpen: boolean;
    closeModal: () => void;
    onClosed?: () => void;
    onConfirm?: () => void;
    selectedSchedule: Schedule;
  } & (
    | { editType: 'create'; shift: ScheduleShift }
    | { editType: 'update'; scheduleWorker: ScheduleWorker }
  ),
): ReactElement {
  const { editType, isOpen, closeModal, selectedSchedule, onClosed } = props;
  const { shift } = editType === 'create' ? props : {};
  const { scheduleWorker } = editType === 'update' ? props : {};
  const {
    jobsiteId,
    tradeOptions: tradeOptionsValues,
    activityCodeOptions: activityCodeOptionsValues,
    developer,
  } = selectedSchedule?.jobsite ?? {};
  const { organizationId: jobsiteOrganizationId } =
    developer.organization ?? {};

  const isMounted = useIsMounted();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isFormDirty, setIsFormDirty] = React.useState(false);

  const [createScheduleWorker] = useCreateScheduleWorkerMutation({
    refetchQueries: [GetScheduleDetailsDocument],
  });

  const [updateScheduleWorker] = useUpdateScheduleWorkerMutation({
    refetchQueries: [GetScheduleDetailsDocument],
  });

  const onSubmit: FormOnSubmit<JobsiteScheduledWorkerFormFields> = async (
    data,
    event,
    dirtyFields,
  ): Promise<void> => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      if (editType === 'create') {
        const input = getAddInput(shift.id, data, dirtyFields);
        await createScheduleWorker({ variables: { input } });
      } else {
        const input = getUpdateInput(scheduleWorker.id, data, dirtyFields);
        await updateScheduleWorker({ variables: { input } });
      }
      closeModal();
      AlertService.alert(
        'success',
        'Success',
        `Worker successfully ${editType === 'create' ? 'added' : 'updated'}`,
      );
    } catch (error) {
      event.preventDefault();
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    } finally {
      if (isMounted()) {
        setIsSaving(false);
      }
    }
  };

  const jobsiteContractorsOptions = useJobsiteContractorsOptions({ jobsiteId });
  const jobsiteWorkersOptions = useJobsiteContractorWorkersOptions({
    jobsiteContractorId: scheduleWorker?.jobsiteContractor.id,
    skip: editType === 'create',
  });

  const tradeOptions = useSelectOptions({
    values: tradeOptionsValues,
    extraValue: scheduleWorker?.trade,
  });
  const activityCodeOptions = useSelectOptions({
    values: activityCodeOptionsValues,
    extraValue: scheduleWorker?.activity,
  });

  const inputs = React.useMemo(
    () =>
      getFormInputsHook({
        jobsiteOrganizationId,
        jobsiteContractorsOptions,
        tradeOptions,
        activityCodeOptions,
      }),
    [
      jobsiteOrganizationId,
      jobsiteContractorsOptions,
      tradeOptions,
      activityCodeOptions,
    ],
  );
  const defaultValues = React.useMemo(
    () =>
      getDefaultValues({
        scheduleWorker,
        jobsiteContractorsOptions,
        jobsiteWorkersOptions,
        tradeOptions,
        activityCodeOptions,
      }),
    [
      scheduleWorker,
      jobsiteContractorsOptions,
      jobsiteWorkersOptions,
      tradeOptions,
      activityCodeOptions,
    ],
  );

  return (
    <ModalForm
      actionText="Save"
      defaultValues={defaultValues}
      inputs={inputs}
      inputsContainerClassName="odin-flex odin-flex-col odin-gap-4"
      onSubmit={onSubmit}
      open={isOpen}
      size="xs"
      setOpen={closeModal}
      onIsDirtyChange={setIsFormDirty}
      afterLeave={onClosed}
      title={`${editType === 'create' ? 'Add' : 'Edit'} scheduled worker`}
      variant="drawer"
      actionButtonEnabled={isFormDirty}
      actionButtonWithSpinner={isSaving}
    />
  );
}
